import React, { useState, useEffect, useCallback } from 'react';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';
import '../../styles/ExerciseLayout.css';

const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${ASSET_BASE_URL}${url}`;
};

const StartersReadingPart4 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        setAnswers(migratedAnswers);
        setIsHydrated(true);
        return;
      }

      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData) {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId || isReviewMode) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated, isReviewMode]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const {
    image_url,
    reading_text,
    shared_options,
  } = exerciseData.content_data;

  const questions = [...exerciseData.questions].sort((a, b) => a.order_index - b.order_index);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const renderStory = () => {
    const parts = reading_text.split(/(\{\{\d+\}\})/g);
    return parts.map((part, index) => {
      const match = part.match(/\{\{(\d+)\}\}/);
      if (match) {
        const qNumber = parseInt(match[1], 10);
        const question = questions.find(q => q.order_index === qNumber);

        if (!question) return part;

        if (qNumber === 0) {
          return <span key={index} className="exercise-example-text mx-1 font-bold text-primary-blue underline decoration-2 underline-offset-4">{question.accepted_answers[0]}</span>;
        }

        const isAnswerCorrect = isReviewMode && (question.is_correct === true || question.is_correct === 'true');
        const showIncorrectAnswerInReview = isReviewMode && !isAnswerCorrect;

        return (
          <span key={index} className="inline-flex items-baseline gap-1">
            <strong className="exercise-gap-number align-middle">({qNumber})</strong>
            {showIncorrectAnswerInReview ? (
              <span className="inline-flex items-center bg-red-50 dark:bg-red-900/20 px-2 py-0.5 rounded-lg border border-red-100 dark:border-red-800 mx-1 align-middle">
                {question.user_answer?.answer
                  ? <span className="text-red-500 line-through decoration-2 mr-2 font-bold">{question.user_answer.answer}</span>
                  : <span className="text-gray-400 mr-2 font-bold">__</span>
                }
                <span className="text-green-600 font-bold">{question.accepted_answers[0]}</span>
              </span>
            ) : (
              <input
                type="text"
                autoComplete="off"
                value={(showHint || isAnswerCorrect) ? question.accepted_answers[0] : (answers[question.id] || '')}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                className={`exercise-input-field exercise-input-text !w-[100px] text-sm px-1 py-1 text-center align-middle ${showHint ? 'hint-input' : ''}`}
                readOnly={showHint || isReviewMode || !isHydrated}
                disabled={isReviewMode || !isHydrated}
              />
            )}
            {isReviewMode && (isAnswerCorrect
              ? <span className="correct-checkmark align-middle scale-75">✓</span>
              : <span className="incorrect-checkmark align-middle scale-75">✗</span>
            )}
            {isReviewMode && (
              <span className="ml-1 align-middle inline-flex"><QuestionLearnButton question={question} size={18} /></span>
            )}
          </span>
        );
      }

      return part;
    });
  };

  return (
    <div className="w-full h-full p-1 md:px-10">
      <div className="relative flex flex-col items-center h-full bg-transparent rounded-2xl p-2 md:p-6 overflow-y-auto custom-scrollbar">
        {/* Top image */}
        {image_url && (
          <div className="bg-white dark:bg-card-dark p-2 rounded-2xl shadow-soft border-2 border-white dark:border-card-dark overflow-hidden shrink-0 mb-4 w-max">
            <img
              alt="Story illustration"
              className="mx-auto rounded-xl w-auto max-w-full max-h-48 object-contain shadow-sm"
              src={resolveUrl(image_url)}
            />
          </div>
        )}

        {/* Reading text with gaps */}
        <div className="max-w-3xl w-full exercise-story-box p-6 md:p-8 shadow-sm border border-slate-100 rounded-3xl bg-white dark:bg-slate-800/40 mb-4">
          <div className="exercise-body-text text-base md:text-lg leading-loose text-slate-700 dark:text-slate-300">
            {renderStory()}
          </div>
        </div>

        {/* Word bank with images */}
        {shared_options && shared_options.length > 0 && (
          <div className="max-w-3xl w-full bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-amber-200 dark:border-slate-600 p-4 md:p-6 shadow-sm">
            <div className="grid grid-cols-4 gap-3 md:gap-4">
              {shared_options.map(option => (
                <div key={option.id} className="flex flex-col items-center gap-1.5 p-2 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                  {option.image_url && (
                    <img
                      src={resolveUrl(option.image_url)}
                      alt={option.image_text || option.id}
                      className="w-full h-14 md:h-16 object-contain"
                    />
                  )}
                  <span className="text-xs md:text-sm font-bold text-slate-700 dark:text-slate-300 text-center">
                    {option.image_text || option.id}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StartersReadingPart4;
